import { RefObject, useContext, useEffect, useRef, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { getFile } from 'api/media';
import { getUploadStrategy } from 'api/upload';
import { UploadContext } from 'components/upload/UploadContext';
import { FileRefMeta, UploadFile, UploadProps, UploadRef } from 'components/upload/types';
import { FilePondFile } from 'filepond';
import { isEmpty } from 'lodash';
import { mediaQueryKeys } from 'queries/media-query-keys';

type PossibleFile = FileRefMeta | UploadFile;
type PossibleFiles = PossibleFile[];

interface UseUploadProps extends Omit<UploadProps, 'defaultFiles'> {
    defaultFiles: PossibleFiles;
}

const useUpload = (props: UseUploadProps, pondRef: RefObject<UploadRef>) => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const {
        strategy = 's3',
        defaultFiles: defaultFilesConsumer = [],
        onInitialized: onInitializedConsumer,
        onUpload: onUploadConsumer,
        onUploadError: onUploadErrorConsumer,
        onreorderfiles: onReorderConsumer,
        onupdatefiles: onUpdateFilesConsumer,
        beforeAddFile: beforeAddFileConsumer,
        uploadFn: uploadFnConsumer,
    } = props;
    const { setFiles } = useContext(UploadContext);
    const defaultFilesRef = useRef(Array.isArray(defaultFilesConsumer) ? defaultFilesConsumer : []);
    const defaultFiles = defaultFilesRef.current;
    const [initMap, setInitMap] = useState(createInitCache(defaultFiles));
    const [didInit, setDidInit] = useState(false);
    const inMemoryDefaultFiles = defaultFiles.filter(file => isUploadFile(file));

    const queries = useQueries({
        queries: defaultFiles
            .filter(file => !isUploadFile(file))
            .map(fileMeta => {
                const url = fileMeta.url!;

                return {
                    queryKey: mediaQueryKeys.file(url),
                    queryFn: async () => {
                        const file = await getFile(url);
                        await pondRef.current?.addFile(file);

                        setInitMap(curr => ({
                            ...curr,
                            [url]: {
                                ...curr[url],
                                loaded: true,
                            },
                        }));

                        return file;
                    },
                    staleTime: 0,
                    retry: 0,
                    enabled: initMap[url].loaded === false,
                };
            }),
    });

    const { data = [], isFetching: isLoadingInMemoryFiles } = useQuery({
        queryKey: mediaQueryKeys.inMemoryFiles(),
        queryFn: async () => {
            const files = inMemoryDefaultFiles.map(file => (file as UploadFile).file);

            return pondRef.current?.addFiles(files) ?? [];
        },
        retry: 0,
        staleTime: Infinity,
    });

    const isLoading =
        queries.length > 0 && queries.some(query => query.isLoading) && isLoadingInMemoryFiles;

    useEffect(() => {
        if (!didInit && !isLoading) {
            setDidInit(true);
            const nextFiles = pondRef?.current?._pond.getFiles() ?? [];
            onInitializedConsumer?.(nextFiles as FilePondFile[]);
        }
    }, [data, didInit, isLoading, onInitializedConsumer, pondRef]);

    useEffect(() => {
        return () => {
            queryClient.invalidateQueries({ queryKey: mediaQueryKeys.inMemoryFiles() });
            queryClient.cancelQueries({ queryKey: mediaQueryKeys.media() });
        };
    }, [queryClient]);

    const { mutateAsync: uploadMutation } = useMutation({
        mutationFn: getUploadStrategy(strategy, uploadFnConsumer),
        onSuccess: url => {
            onUploadConsumer?.(url);
        },
        onError: (e: Error) => {
            onUploadErrorConsumer?.(e);
        },
    });

    const onBeforeAddFile: UploadProps['beforeAddFile'] = async file => {
        try {
            let url = file.filename;
            const existingFile = defaultFiles.find(
                defaultFile => defaultFile.url === file.filename,
            );

            if (!!existingFile?.id) {
                file.setMetadata('id', existingFile.id);
            } else {
                url = await uploadMutation(file.file as File);
            }
            file.setMetadata('url', url);

            beforeAddFileConsumer?.(file);

            return true;
        } catch (_) {
            return false;
        }
    };

    const onUpdate: UploadProps['onupdatefiles'] = files => {
        onUpdateFilesConsumer?.(files);
        setFiles(files);
    };

    const onReorder: UploadProps['onreorderfiles'] = files => {
        onReorderConsumer?.(files);
        onUpdate(files);
    };

    const onError: UploadProps['onerror'] = (error, file) => {
        // @ts-ignore
        toast.error(error?.main);
        pondRef.current?.removeFile(file);
    };

    const onWarning: UploadProps['onwarning'] = error => {
        if (error?.body?.includes('Max files')) {
            toast.error('Max file limit reached');
        }
    };

    const orderFiles: UploadProps['itemInsertLocation'] = (file1, file2) => {
        const file1Index = defaultFiles.findIndex(f => f.id === file1?.getMetadata('id'));
        const file2Index = defaultFiles.findIndex(f => f.id === file2?.getMetadata('id'));

        if (file1Index === -1) {
            return 1;
        }

        if (file1Index > file2Index) {
            return 1;
        }

        if (file1Index < file2Index) {
            return -1;
        }

        return 0;
    };

    return {
        orderFiles,
        onReorder,
        onBeforeAddFile,
        onError,
        onWarning,
        onUpdate,
        isLoading,
    };
};

interface InitCache {
    [key: string]: {
        loaded: boolean;
    };
}

const createInitCache = (urls: (FileRefMeta | UploadFile)[]) => {
    return urls.reduce((curr, prev) => {
        if (isUploadFile(prev)) {
            const possibleUrl = prev.getMetadata('url');
            const url = !isEmpty(possibleUrl) ? possibleUrl : prev.filename;

            curr[url] = {
                loaded: true,
            };
        } else {
            curr[prev.url] = {
                loaded: false,
            };
        }
        return curr;
    }, {} as InitCache);
};

function isUploadFile(possibleFile: FileRefMeta | UploadFile): possibleFile is UploadFile {
    return possibleFile.hasOwnProperty('getMetadata');
}

export { useUpload };
